import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tbody, Tr, Td, Box, Skeleton } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import { useNavigate } from 'react-router-dom';
import { EmptyField } from 'components/elements//EmptyField';
import CompanyCell from 'components/elements/LeadsTable/cells/CompanyCell';
import LeadsTableValue from 'components/elements/LeadsTable/cells/Value';
import { LEADS_TABLE_COLUMN_IDS } from 'components/elements/LeadsTable/constants';
import LoadingState from 'components/elements/LeadsTable/LoadingState';
import Tooltip from 'components/elements/Tooltip';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import LotSize from 'components/widgets/ProjectDetails/ProjectInfo/LotSize';
import WorkType from 'components/widgets/ProjectDetails/ProjectInfo/WorkType';
import SaveProject from 'components/widgets/SaveProject';
import { useSavedProjects } from 'hooks/useSavedProjects';
import { ProjectListVerticalSection } from 'pages/ProjectInsights/ProjectListVerticalSection';
import { useCurrentLeadQuery } from 'state/queryParams';
import { PaginatedProject } from 'types';
import { SavedProject } from 'types/SavedProject';
import { trackProjectListClick } from 'utils/analytics';
import { ProjectGroupTypes } from 'utils/analytics/constants';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';
import { formatDate, isDate24HoursOld } from 'utils/date';
import { capitalizeWords } from 'utils/format';
import { ddpUrl, pdpUrl } from 'utils/navigation';
import type { TColumnProps, TLeadContext } from 'components/elements/LeadsTable/types';

type LeadTableNote = {
  note: string;
  first_name: string;
  is_mine: boolean;
};

const stickyColumnStyle = {
  'tr>td:first-of-type': {
    position: 'sticky',
    left: '0',
    zIndex: Z_INDEX_DICT.LEAD_TABLE_LEFT_COLUMN_BODY,
  },
};

const rowStyle = (isSelected: boolean, isClickable: boolean) => ({
  backgroundColor: 'transparent',
  '&:hover': {
    cursor: isClickable ? 'pointer' : 'normal',
    td: {
      background: Colors.ArmyGreen100,
    },
    '.open-in-new-tab-link': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  td: {
    backgroundColor: isSelected ? Colors.ArmyGreen100 : Colors.White,
  },
  '.open-in-new-tab-link': {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.2s ease',
  },
});

const addressRow = {
  justifyContent: 'left',
  gridGap: 1,
};

const dateText = {
  lineHeight: '14px',
  textTransform: 'capitalize',
  color: Colors.ArmyGreen500,
};

const addressText = {
  textTransform: 'capitalize',
};

const subDescriptionStyle = {
  color: Colors.TextQuiet,
};

const descriptionStyle = {
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3, // Limit text to 3 lines
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
};

const rowOpenInNewTabButtonStyle = {
  color: Colors.TextQuiet,
  minWidth: 'unset',
  _hover: {
    color: Colors.Black800,
  },
};

type LatestNoteCellProps = {
  noteText: string;
  displayAuthorName: string;
};

const LatestNoteCell = ({ noteText, displayAuthorName }: LatestNoteCellProps) => {
  const noteBoxRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = noteBoxRef.current;
    if (!el) {
      setIsTruncated(false);

      return () => {};
    }

    const compute = () => {
      // With `-webkit-line-clamp`, the element's `scrollHeight` typically retains the
      // unclamped content height.
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    };

    compute();

    const resizeObserver = new ResizeObserver(() => {
      compute();
    });
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [noteText]);

  return (
    <Tooltip
      tooltipText={noteText}
      fullWidth
      shouldRenderTooltip={isTruncated}
      isDisabled={!isTruncated}
    >
      <Box>
        {noteText ? (
          <Box ref={noteBoxRef} sx={descriptionStyle}>
            <Text.Body color={Colors.TextQuiet}>{noteText}</Text.Body>
          </Box>
        ) : null}
        {displayAuthorName ? (
          <Box mt="padding_4">
            <FlexRow alignItems="center" justifyContent="flex-start" sx={{ gap: '4px' }}>
              <Icon name={Icon.Name.User} fill={Colors.TextQuiet} size={12} />
              <Text.SmallLabel color={Colors.TextQuiet}>
                {displayAuthorName}
              </Text.SmallLabel>
            </FlexRow>
          </Box>
        ) : null}
      </Box>
    </Tooltip>
  );
};

type LatestDescriptionCellProps = {
  tooltipText: string;
  titleText?: string;
  bodyText: string;
};

const LatestDescriptionCell = ({
  tooltipText,
  titleText,
  bodyText,
}: LatestDescriptionCellProps) => {
  const clampedBoxRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = clampedBoxRef.current;
    if (!el) {
      setIsTruncated(false);

      return () => {};
    }

    const compute = () => {
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    };

    compute();

    const resizeObserver = new ResizeObserver(() => {
      compute();
    });
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [tooltipText, bodyText]);

  return (
    <Tooltip
      tooltipText={tooltipText}
      fullWidth
      shouldRenderTooltip={isTruncated}
      isDisabled={!isTruncated}
    >
      <Box sx={descriptionStyle} ref={clampedBoxRef}>
        {titleText ? (
          <Box mb="padding_4">
            <Text.BodyBold>{titleText}</Text.BodyBold>
          </Box>
        ) : null}
        {bodyText ? <Text.Body sx={subDescriptionStyle}>{bodyText}</Text.Body> : null}
      </Box>
    </Tooltip>
  );
};

type Props = TColumnProps & {
  items: PaginatedProject[];
  hasClickableRows: boolean;
  leadContext?: TLeadContext;
  isLoading: boolean;
  visibleColumnOrder: string[];
  notesByEntityId: Record<string, LeadTableNote>;
  isNotesLoading: boolean;
  showSpacerColumn?: boolean;
};

const TableBody = ({
  items,
  hasClickableRows,
  leadContext,
  isLoading,
  visibleColumnOrder,
  notesByEntityId,
  isNotesLoading,
  showSpacerColumn = false,
}: Props) => {
  const actionRef = useRef<HTMLTableCellElement>(null);
  const descriptionRef = useRef<HTMLTableCellElement>(null);
  const [lead, setLead] = useCurrentLeadQuery();
  const navigate = useNavigate();
  const { getSavedProjectsForSharedCollection } = useSavedProjects();
  const sharedCollectionId =
    leadContext?.groupType === ProjectGroupTypes.SHARED_COLLECTION
      ? leadContext?.groupId
      : undefined;
  const savedProjectsQuery = getSavedProjectsForSharedCollection(
    sharedCollectionId ?? '',
    {
      enabled: !!sharedCollectionId,
    },
  );
  const savedProjectsMap = (savedProjectsQuery.data ?? {}) as Record<
    string,
    SavedProject
  >;

  const generateElementId = useCallback((project_slug: string) => {
    return `group-lead-${project_slug}`;
  }, []);

  useEffect(() => {
    if (lead) {
      const element = document.getElementById(generateElementId(lead));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, []);

  const isSelected = (entityId: string) => {
    return lead === entityId;
  };

  const onRowClick = ({
    event,
    projectSlug,
    documentId,
    clickText,
    groupId,
    groupType,
  }: {
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>;
    projectSlug?: string;
    documentId?: string;
    clickText?: string;
    groupId?: string;
    groupType?: string;
  }) => {
    if (hasClickableRows) {
      trackProjectListClick({
        projectId: projectSlug,
        documentId,
        groupId,
        groupType,
        clickText,
      });
      let targetUrl: string = '';

      if (documentId) {
        setLead(documentId);
        targetUrl = ddpUrl(documentId);
      } else if (projectSlug) {
        setLead(projectSlug);
        targetUrl = pdpUrl({ projectSlug });
      }

      if (!targetUrl) {
        return;
      }

      if (
        event.metaKey || // Cmd (Mac)
        event.ctrlKey || // Ctrl (Windows/Linux)
        event.shiftKey || // Shift+Click
        event.altKey // Alt
      ) {
        window.open(targetUrl, '_blank', 'rel=noopener noreferrer');
      } else {
        navigate(targetUrl);
      }
    }
  };

  const renderCell = useCallback(
    (columnId: string, item: PaginatedProject) => {
      const {
        name,
        summary,
        address,
        vertical,
        status,
        latest_activity,
        startdate,
        slug: project_slug,
        document_id,
        companies,
        latest_companies_array,
        city,
        state,
        parcel_size_info,
        work_type_names,
        cost,
        predicted_project_value,
      } = item;
      switch (columnId) {
        case 'ACTION': {
          return (
            <SaveProject
              projectSlug={project_slug}
              documentId={document_id}
              savedProject={savedProjectsMap[project_slug]}
              isSharedCollection={!!sharedCollectionId}
              displayBorders={{ savedMenu: true, unsavedMenu: false }}
            />
          );
        }
        case 'ADDRESS':
          return (
            <FlexRow sx={addressRow}>
              <Text.SmallLabel color={Colors.ForestGreen900}>
                {isDate24HoursOld(startdate || '') && 'new'}
              </Text.SmallLabel>
              <FlexColumn>
                <Text.BodyBold sx={addressText}>{address}</Text.BodyBold>
                <Text.Body color={Colors.ArmyGreen700}>{`${city}, ${state}`}</Text.Body>
              </FlexColumn>
            </FlexRow>
          );
        case 'STATUS':
          return <Text.Body>{status}</Text.Body>;
        case 'LATEST_PROJECT_STAGE':
          return (
            <>
              <FlexRow>
                <Text.BodyBold>{latest_activity?.type}</Text.BodyBold>
              </FlexRow>
              <FlexRow>
                {latest_activity?.status ? (
                  <Text.Body color={Colors.ArmyGreen700}>
                    {capitalizeWords(latest_activity?.status)}
                  </Text.Body>
                ) : (
                  <EmptyField />
                )}
              </FlexRow>
            </>
          );
        case 'DESCRIPTION':
          return summary ||
            latest_activity?.description ||
            name ||
            latest_activity?.name ? (
            <LatestDescriptionCell
              tooltipText={summary || latest_activity?.description || ''}
              titleText={name || latest_activity?.name}
              bodyText={summary || latest_activity?.description || ''}
            />
          ) : (
            <EmptyField />
          );
        case LEADS_TABLE_COLUMN_IDS.MY_NOTES: {
          if (isNotesLoading) {
            return <Skeleton height="16px" width="100%" maxWidth="200px" />;
          }
          const noteKey =
            item.id !== undefined && item.id !== null
              ? String(item.id)
              : item.document_id;
          const myNote = noteKey ? notesByEntityId[noteKey] : undefined;
          const noteText = myNote?.note ?? '';
          const authorFirstName = myNote?.first_name ?? '';
          const isMine = myNote?.is_mine ?? false;
          let displayAuthorName = authorFirstName;
          if (isMine) {
            displayAuthorName = authorFirstName ? `${authorFirstName} (you)` : 'You';
          }

          return myNote ? (
            <LatestNoteCell noteText={noteText} displayAuthorName={displayAuthorName} />
          ) : (
            <EmptyField />
          );
        }
        case 'TOTAL_VALUE':
          return (
            <LeadsTableValue
              cost={cost}
              predictedProjectValue={predicted_project_value}
            />
          );
        case 'WORK_TYPE':
          return (
            <Tooltip tooltipText={work_type_names?.join(', ') || ''} fullWidth>
              <Box sx={descriptionStyle}>
                <WorkType workType={work_type_names || []} />
              </Box>
            </Tooltip>
          );
        case 'LOT_SIZE':
          return <LotSize lotSizeSqft={Number(parcel_size_info?.lotsize_sqft)} />;
        case 'SECTORS':
          return (
            <FlexRow
              justifyContent="start"
              alignItems="center"
              className="textSmallLight"
            >
              {vertical ? (
                <ProjectListVerticalSection vertical={vertical} />
              ) : (
                <EmptyField />
              )}
            </FlexRow>
          );
        case 'COMPANY':
          return (
            <CompanyCell
              companyListString={companies}
              latestCompaniesArray={latest_companies_array}
            />
          );
        case 'LAST_UPDATED':
          return latest_activity?.date ? (
            <Text.Body className="textSmallLighter" sx={dateText}>
              {formatDate(latest_activity?.date)}
            </Text.Body>
          ) : (
            <EmptyField />
          );
        case 'DATE_CREATED':
          return startdate ? (
            <Text.Body className="textSmallLighter" sx={dateText}>
              {formatDate(startdate)}
            </Text.Body>
          ) : (
            <EmptyField />
          );
        default:
          return null;
      }
    },
    [
      savedProjectsMap,
      sharedCollectionId,
      notesByEntityId,
      isNotesLoading,
      leadContext?.groupId,
      leadContext?.groupType,
    ],
  );

  const numOfColumns = visibleColumnOrder.length;

  if (isLoading) {
    return (
      <LoadingState numberOfColumns={numOfColumns} showSpacerColumn={showSpacerColumn} />
    );
  }

  return (
    <Tbody sx={stickyColumnStyle}>
      {items.map((item) => {
        const project_slug = item.slug;
        const { document_id } = item;

        const entityId = project_slug || document_id || '';

        return (
          <Tr
            id={generateElementId(entityId)}
            key={`leads-table-body-table-row-${entityId}`}
            onClick={(event) =>
              onRowClick({
                event,
                projectSlug: project_slug,
                documentId: document_id,
                clickText: undefined,
                groupId: leadContext?.groupId,
                groupType: leadContext?.groupType,
              })
            }
            sx={rowStyle(isSelected(entityId || ''), hasClickableRows)}
          >
            {visibleColumnOrder.map((columnId) => {
              let tdRef: React.RefObject<HTMLTableCellElement> | undefined;
              if (columnId === LEADS_TABLE_COLUMN_IDS.ACTION) tdRef = actionRef;
              else if (columnId === LEADS_TABLE_COLUMN_IDS.DESCRIPTION)
                tdRef = descriptionRef;
              let targetUrl = '';
              if (document_id) {
                targetUrl = ddpUrl(document_id);
              } else if (project_slug) {
                targetUrl = pdpUrl({ projectSlug: project_slug });
              }
              const isActionColumn = columnId === LEADS_TABLE_COLUMN_IDS.ACTION;
              let tdSx: { width: string } | { position: 'relative' } | undefined;
              if (columnId === LEADS_TABLE_COLUMN_IDS.LATEST_PROJECT_STAGE) {
                tdSx = { width: 'fit-content' };
              } else if (isActionColumn) {
                tdSx = { position: 'relative' };
              }

              return (
                <Td key={columnId} ref={tdRef} sx={tdSx}>
                  {renderCell(columnId, item)}
                  {isActionColumn && targetUrl ? (
                    <Button
                      className="open-in-new-tab-link"
                      sx={rowOpenInNewTabButtonStyle}
                      variant="unstyled"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        trackProjectListClick({
                          projectId: project_slug,
                          documentId: document_id,
                          groupId: leadContext?.groupId,
                          groupType: leadContext?.groupType,
                          clickText: 'Open in new tab',
                        });
                        window.open(targetUrl, '_blank', 'noopener,noreferrer');
                      }}
                      aria-label="Open in new tab"
                    >
                      <Text sx={{ display: 'flex', alignItems: 'center' }}>
                        Open in new tab <Icon name={Icon.Name.ArrowDiagonal} />
                      </Text>
                    </Button>
                  ) : null}
                </Td>
              );
            })}
            {showSpacerColumn && <Td />}
          </Tr>
        );
      })}
    </Tbody>
  );
};

export default TableBody;
